import { Component, HostListener, inject, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { v4 as uuid } from 'uuid';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { TenantContextService } from '../../services/tenant-context.service';
import { uniqueFormArrayValidator } from '../../../../core/validators/form-array.validator';
import { CreateDataStoreRequest } from '../../tabs/data-lakes-tab/models/create-data-store.request';
import { catchError, combineLatest, of, switchMap } from 'rxjs';
import { GetDataStoreRequest } from '../../tabs/data-lakes-tab/models/get-data-store-request';
import { DataStore } from '../../tabs/data-lakes-tab/models/data-store.model';
import { ToastService } from '../../../../core/services/toast.service';
import { DataStoreService } from '../../tabs/data-lakes-tab/services/data-store.service';

@Component({
  selector: 'app-add-landing-zone-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    LoadingSpinnerComponent
  ],
  templateUrl: './add-landing-zone-dialog.component.html',
  styleUrl: './add-landing-zone-dialog.component.css'
})
export class AddLandingZoneDialogComponent {
  
  dataStoreService = inject(DataStoreService);
  tenantContext = inject(TenantContextService);
  toastService = inject(ToastService);
  
  addLandingZoneForm: FormGroup;

  isSubmitting = signal<boolean>(false);

  constructor (private dialogRef: MatDialogRef<AddLandingZoneDialogComponent>, private fb: FormBuilder) {
    this.addLandingZoneForm = this.fb.group({
      // each landing zone FormGroup must have an unique value when compared to each other
      landingZone: this.fb.array([this.createLandingZoneInput()],[uniqueFormArrayValidator]),
    });
  }

  get landingZoneInputs(): FormArray {
    return this.addLandingZoneForm.get("landingZone") as FormArray;
  }

  createLandingZoneInput(): FormGroup {
    return this.fb.group({
      // FormGroups are given a UUID to provide robust index tracker to allow for deletion of FormGroups
      id: uuid(),
      value: ['', [
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9-]{3,63}$/)
      ]]
    });
  }

  addLandingZone(): void {
    if (this.landingZoneInputs.length < 10) {
      this.landingZoneInputs.push(this.createLandingZoneInput());
    }
  }

  removeLandingZoneInput(index: number): void {
    if (this.landingZoneInputs.length > 1) {
      this.landingZoneInputs.removeAt(index);
    }
  }

  onSubmit(): void {
    if (this.addLandingZoneForm.valid) {
        this.isSubmitting.set(true);

        const landingZoneNames: string[] = [];

        // get the landing zone name (value) for each FormGroup inside the FromArray
        this.landingZoneInputs.controls.forEach((control: AbstractControl) => {
        const formGroup = control as FormGroup;
        landingZoneNames.push(formGroup.value.value);
        });

        const tenantId = this.tenantContext.tenant()?.tenantId;
        if (!tenantId) return;

        const requests: CreateDataStoreRequest[] = [];
        landingZoneNames.forEach(name => {
        const request: CreateDataStoreRequest = {
            storeName: name,
            tenantId: tenantId
        }
        requests.push(request);
        });

        const createCalls = requests.map((request) => 
        this.dataStoreService.createLandingZone(request).pipe(
            switchMap((result: string) => {
            const getRequest: GetDataStoreRequest = {
                tenantId: tenantId,
                dataStoreId: result
            }
            return this.dataStoreService.getLandingZone(getRequest).pipe(
                catchError(error => {
                return of({ error, failed: true, stage: "get", result});
                })
            )
            }),
            catchError(error => {
            return of({ error, failed: true, stage: "create", request});
            })
        )
        );

        combineLatest(createCalls).subscribe(results => {
            const createdLandingZones: DataStore[] = results.filter(
                (result): result is DataStore => !('failed' in result)
            );
            const failures = results.filter(result => 'failed' in result);
            if (failures.length > 0) {
              let failureMessage = "Failed to create for the following landing zone/s: ";
              failures.forEach(failure => {
                if (failure.error.error.error === undefined)
                {
                  failureMessage = "Internal Server Error. Please try again later."
                }
                else {
                  failureMessage = failureMessage + "\n - " + failure.error.error.error;
                }
            });

          this.toastService.showError(failureMessage);
            }
            this.isSubmitting.set(false);
            this.dialogRef.close(createdLandingZones);
        });
    }
  }

  // display default browser popup on unloading page during form submission
  // does not warn navigating to another page of neuro utils as it is a SPA and would require a route guard for it
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.isSubmitting()) {
      $event.preventDefault();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
